import { useEffect, useRef, useState, type FormEvent } from "react";
import { useApp, useUiDispatch } from "@/app/AppContext";
import { StatusMessage, type Message } from "@/components/StatusMessage";
import { saveAssessments } from "@/data/systemAnalysisRepository";
import {
  ALL_LOCATIONS_ID,
  SYSTEM_SCORE_SCALE,
  type SystemAnalysis,
  type SystemCriterion,
} from "@/domain/systemAnalysis";
import type { SourceRow } from "@/domain/types";
import { format } from "@/i18n/translator";
import { useI18n } from "@/i18n/useI18n";
import { pick, statusLabelKey } from "./useSystemAnalysis";

/** Source columns written for each criterion (kept compatible with the Annex 4 workbook). */
const DETAILS_COLUMN = "Data_t_EN#Assessment details";
const NEXT_LEVEL_COLUMN = "Data_t#What was not met, and what the next level would require";

interface Draft {
  score: string;
  details: string;
  nextLevel: string;
}

const EMPTY_DRAFT: Draft = { score: "", details: "", nextLevel: "" };

function toDraft(row: SourceRow): Draft {
  return {
    score: String(row.Criterion_assessment ?? "").match(/^([0-6])/)?.[1] ?? "",
    details: String(row[DETAILS_COLUMN] ?? ""),
    nextLevel: String(row[NEXT_LEVEL_COLUMN] ?? ""),
  };
}

/** Unique criteria of one year's analysis (from the "all locations" view). */
function criteriaOf(analysis: SystemAnalysis): SystemCriterion[] {
  const items = (analysis.domainsByLocation[ALL_LOCATIONS_ID] ?? []).flatMap(
    (domain) => domain.items,
  );
  return [...new Map(items.map((item) => [item.id, item])).values()];
}

interface SystemAnalysisDialogProps {
  analysis: SystemAnalysis | null;
  /** Unique criteria of the analysis being edited (from the "all locations" view). */
  criteria: SystemCriterion[];
  /** Selectable locations; a local admin gets exactly their own. */
  locations: string[];
  initialLocation: string;
  /** Editing an existing location/year: year and location are fixed. */
  editing: boolean;
  /** Year for a new analysis; defaults to the current year. */
  initialYear?: string;
  onClose(): void;
}

export function SystemAnalysisDialog({
  analysis,
  criteria,
  locations,
  initialLocation,
  editing,
  initialYear,
  onClose,
}: SystemAnalysisDialogProps) {
  const { t, language } = useI18n();
  const { user, data } = useApp();
  const dispatch = useUiDispatch();
  const [year, setYear] = useState(
    String(editing && analysis ? analysis.year : (initialYear ?? new Date().getFullYear())),
  );
  const [location, setLocation] = useState(initialLocation);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const draftsFor = (target: string): Record<string, Draft> =>
    Object.fromEntries(
      (analysis?.assessmentsByLocation?.[target] ?? []).map((row) => [
        row.criterion_id,
        toDraft(row.row_data),
      ]),
    );
  const [drafts, setDrafts] = useState(() => draftsFor(initialLocation));

  // Criteria are stored per year. Only years with criteria of their own are
  // offered. A year that already has criteria (its own or a copy) keeps them;
  // a new year gets a copy of the chosen year's criteria when it is saved.
  const criterionSets = data.systemData.criteriaSets ?? [];
  const [chosenSet, setChosenSet] = useState(() =>
    criterionSets.length === 1 ? criterionSets[0]!.year : "",
  );
  const fixedSource = data.systemData.criteriaSourceByYear?.[year.trim()];
  const asksForCriteria = !editing && criterionSets.length > 0;
  const criteriaYear = asksForCriteria ? (fixedSource ?? (chosenSet || null)) : null;
  const yearAnalysis = data.systemData.analyses.find((entry) => String(entry.year) === year.trim());
  const shownCriteria = !asksForCriteria
    ? criteria
    : (criterionSets.find((set) => set.year === criteriaYear)?.criteria ??
      (fixedSource && yearAnalysis ? criteriaOf(yearAnalysis) : []));

  useEffect(() => {
    yearRef.current?.focus();
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const update = (id: string, patch: Partial<Draft>) =>
    setDrafts((current) => ({ ...current, [id]: { ...EMPTY_DRAFT, ...current[id], ...patch } }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const target = user?.role === "localadmin" ? (user.location ?? "") : location;
    if (!/^\d{4}$/.test(year.trim()) || !target)
      return setMessage({ kind: "error", text: t("systemCreateAnalysisError") });
    const exists = data.systemData.analyses.some(
      (entry) =>
        String(entry.year) === year.trim() &&
        entry.locations.some((loc) => loc.locationId === target),
    );
    if (exists && !editing)
      return setMessage({ kind: "error", text: t("systemCreateAnalysisDuplicate") });
    if (asksForCriteria && !criteriaYear)
      return setMessage({ kind: "error", text: t("systemCreateAnalysisCriteriaSetRequired") });

    const existingRows = new Map(
      (analysis?.assessmentsByLocation?.[target] ?? []).map((row) => [
        row.criterion_id,
        row.row_data,
      ]),
    );
    const rows = shownCriteria.map((item) => {
      const draft = drafts[item.id] ?? EMPTY_DRAFT;
      return {
        criterion_id: item.id,
        row_data: {
          ...existingRows.get(item.id),
          Year: year.trim(),
          Location: target,
          Criterion_ID: item.id,
          Criterion_assessment: draft.score,
          [DETAILS_COLUMN]: draft.details.trim(),
          [NEXT_LEVEL_COLUMN]: draft.nextLevel.trim(),
        },
      };
    });

    setBusy(true);
    try {
      await saveAssessments(year.trim(), target, rows, criteriaYear);
      dispatch({ type: "selectSystemAnalysis", analysisId: `annex4_${year.trim()}` });
      dispatch({ type: "selectSystemLocation", locationId: target });
      const refreshed = await data.reload();
      window.alert(
        refreshed ? t("systemCreateAnalysisSuccess") : t("systemCreateAnalysisRefreshError"),
      );
      onClose();
    } catch (error) {
      setMessage({
        kind: "error",
        text: `${t("systemCreateAnalysisError")} (${(error as Error).message})`,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      id="system-analysis-create-modal"
      className="system-analysis-modal"
      role="presentation"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <form
        id="system-analysis-create-form"
        className="system-analysis-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="system-analysis-create-title"
        aria-describedby="system-analysis-create-description"
        onSubmit={submit}
      >
        <header className="system-analysis-dialog-header">
          <div className="system-analysis-dialog-heading">
            <div>
              <h2 id="system-analysis-create-title">
                {editing ? t("systemEditAnalysisTitle") : t("systemCreateAnalysisTitle")}
              </h2>
              <p id="system-analysis-create-description">{t("systemCreateAnalysisDescription")}</p>
            </div>
            <button
              type="button"
              className="system-analysis-dialog-close"
              aria-label={t("closeDetails")}
              onClick={onClose}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <div id="system-analysis-create-fields" className="system-analysis-create-fields">
            <label>
              {t("systemCreateAnalysisYear")}
              <input
                ref={yearRef}
                type="number"
                min={1900}
                max={2200}
                step={1}
                required
                readOnly={editing}
                value={year}
                onChange={(event) => setYear(event.target.value)}
              />
            </label>
            <label>
              {t("systemCreateAnalysisLocation")}
              <select
                required
                disabled={editing || user?.role === "localadmin"}
                value={location}
                onChange={(event) => {
                  setLocation(event.target.value);
                  setDrafts(draftsFor(event.target.value));
                }}
              >
                {locations.map((entry) => (
                  <option key={entry} value={entry}>
                    {entry}
                  </option>
                ))}
              </select>
            </label>
            {asksForCriteria && (
              <label>
                {t("systemCreateAnalysisCriteriaSet")}
                <select
                  required
                  disabled={!!fixedSource}
                  value={criteriaYear ?? ""}
                  onChange={(event) => setChosenSet(event.target.value)}
                >
                  <option value="" disabled>
                    {t("systemCreateAnalysisChooseCriteriaSet")}
                  </option>
                  {criterionSets.map((set) => (
                    <option key={set.year} value={set.year}>
                      {format(t("systemCreateAnalysisCriteriaSetOption"), {
                        year: set.year,
                        count: set.criteria.length,
                      })}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </header>
        <div className="system-analysis-dialog-body">
          <div id="system-analysis-create-criteria" className="system-analysis-criteria-list">
            {!shownCriteria.length && (
              <p className="system-analysis-criteria-hint">
                {t("systemCreateAnalysisCriteriaSetRequired")}
              </p>
            )}
            {shownCriteria.map((item) => {
              const draft = drafts[item.id] ?? EMPTY_DRAFT;
              const scoreTexts = (language === "en" ? item.scoreTextsEn : item.scoreTextsNo) ?? {};
              const fallbackTexts =
                (language === "en" ? item.scoreTextsNo : item.scoreTextsEn) ?? {};
              const description = draft.score
                ? scoreTexts[draft.score] || fallbackTexts[draft.score] || ""
                : "";
              const criterionText = pick(language, item.criterionNo, item.criterionEn);
              return (
                <section key={item.id} className="system-analysis-criterion">
                  <div className="system-analysis-criterion-heading">
                    <span className="system-analysis-criterion-id">{item.id}</span>
                    <h3 className="system-analysis-criterion-title">
                      {pick(language, item.titleNo, item.titleEn) || item.id}
                    </h3>
                  </div>
                  {criterionText && (
                    <p className="system-analysis-criterion-description">{criterionText}</p>
                  )}
                  <div className="system-analysis-score-field">
                    <label>
                      {t("systemAssessment")}
                      <select
                        value={draft.score}
                        onChange={(event) => update(item.id, { score: event.target.value })}
                      >
                        <option value="">{t("systemCreateAnalysisChooseScore")}</option>
                        {SYSTEM_SCORE_SCALE.map((score) => (
                          <option key={score.number} value={score.number}>
                            {`${score.number} – ${t(statusLabelKey(score.status))}`}
                          </option>
                        ))}
                      </select>
                    </label>
                    {description && (
                      <p className="system-analysis-score-description" aria-live="polite">
                        {description}
                      </p>
                    )}
                  </div>
                  <div className="system-analysis-writing-fields">
                    <label>
                      {t("systemAnalysisAssessmentDetails")}
                      <textarea
                        value={draft.details}
                        onChange={(event) => update(item.id, { details: event.target.value })}
                      />
                    </label>
                    <label>
                      {t("systemAnalysisNextLevel")}
                      <textarea
                        value={draft.nextLevel}
                        onChange={(event) => update(item.id, { nextLevel: event.target.value })}
                      />
                    </label>
                  </div>
                </section>
              );
            })}
          </div>
        </div>
        <div className="system-analysis-dialog-actions">
          <StatusMessage message={message} />
          <button type="button" onClick={onClose}>
            {t("systemCreateAnalysisCancel")}
          </button>
          <button type="submit" className="system-analysis-create-btn" disabled={busy}>
            {t("systemCreateAnalysisSave")}
          </button>
        </div>
      </form>
    </div>
  );
}
